import { line } from 'd3-shape';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  hoveredBeatSelector,
  pinnedBeatSelector,
} from '../../../beats/beats.selector';
import { Scale } from '../../../plot/types/scale';
import { roundPixel } from '../../../utils/math';
import { ClusterBeat } from '../../clustering.types';
import { LorenzGraphDatum } from './LorenzGraphDatum';

interface Props {
  xScale: Scale;
  yScale: Scale;
  beats: ClusterBeat[];
}
export const LorenzGraph: React.FC<Props> = ({ xScale, yScale, beats }) => {
  const pinnedBeat = useSelector(pinnedBeatSelector);
  const hoveredBeat = useSelector(hoveredBeatSelector);

  const LorenzPoints = useMemo(() => {
    return beats
      .map((beat) => {
        if (beat.currentRR != null && beat.nextRR != null) {
          return {
            x: xScale(beat.currentRR),
            y: yScale(beat.nextRR),
            id: beat.id,
          };
        }
        return null;
      })
      .filter(
        (beat): beat is { x: number; y: number; id: number } => beat != null,
      )
      .map((point) => {
        return (
          <LorenzGraphDatum
            key={point.id}
            point={point}
            opacity={0.3}
            stroke='black'
          />
        );
      });
  }, [beats, xScale, yScale]);

  const pathData = useMemo(() => {
    const lineFn = line<number>()
      .x((n) => roundPixel(xScale(n)))
      .y((n) => roundPixel(yScale(n)));
    return lineFn([0, 2000]) || undefined;
  }, [xScale, yScale]);
  return (
    <g>
      <path
        d={pathData}
        strokeWidth={1}
        opacity={0.3}
        stroke='black'
        fill='none'
      />
      {LorenzPoints}
      {hoveredBeat?.currentRR && hoveredBeat?.nextRR && (
        <LorenzGraphDatum
          point={{
            id: hoveredBeat.id,
            x: xScale(hoveredBeat.currentRR),
            y: yScale(hoveredBeat.nextRR),
          }}
          opacity={1}
          stroke='purple.500'
        />
      )}
      {pinnedBeat?.currentRR && pinnedBeat?.nextRR && (
        <LorenzGraphDatum
          point={{
            id: pinnedBeat.id,
            x: xScale(pinnedBeat.currentRR),
            y: yScale(pinnedBeat.nextRR),
          }}
          opacity={1}
          stroke='yellow.500'
        />
      )}
    </g>
  );
};
